import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import CalendarModal from '../components/CalendarModal';
import WhatsAppButton from '../components/WhatsAppButton';
// Create a mapping for industry thumbnail images
const industryThumbnails = {
  hospitals: encodeURI('/Photos for Thumbnails_250819/Hospitals & Healthcare/194132170_l.jpg'),
  events: encodeURI('/Photos for Thumbnails_250819/Events & Exhibitions /205736134_l.jpg'),
  corporate: encodeURI('/Photos for Thumbnails_250819/Corporate & Enterprise/243038458_l.jpg'),
  education: encodeURI('/Photos for Thumbnails_250819/Education/161133486_l.jpg'),
  fnb: encodeURI('/Photos for Thumbnails_250819/F&B/112988731_l.jpg'),
  hotels: encodeURI('/Photos for Thumbnails_250819/Hotel/155883784_l.jpg'),
  lifestyle: encodeURI('/Photos for Thumbnails_250819/Lifestyle Retail/206475442_l.jpg'),
  malls: encodeURI('/Photos for Thumbnails_250819/Malls & Marketplaces/157201568_l.jpg'),
  tourism: encodeURI('/Photos for Thumbnails_250819/Travel Tourism/131184682_l.jpg'),
  places: encodeURI('/Photos for Thumbnails_250819/Places of Interest/97640914_l.jpg'),
  nightlife: encodeURI('/Photos for Thumbnails_250819/Events & Exhibitions /213939337_l.jpg'),
  nonprofit: encodeURI('/Photos for Thumbnails_250819/Corporate & Enterprise/235399802_l.jpg')
};

const heroImages = [
  '/Moments Hero Banner (2560 x 1440 px)/6.png',
  '/Moments Hero Banner (2560 x 1440 px)/7.png',
  '/Moments Hero Banner (2560 x 1440 px)/8.png',
  '/Moments Hero Banner (2560 x 1440 px)/9.png',
  '/Moments Hero Banner (2560 x 1440 px)/10.png',
  '/Moments Hero Banner (2560 x 1440 px)/11.png'
];

const industries = [
  { path: "hospitals", label: "Hospitals & Healthcare", icon: "🏥", description: "Engage patients & families with branded photobooks. From QR code scan to printed photobook in 5 minutes. Deepen loyalty through personalized keepsakes." },
  { path: "events", label: "Events & Exhibitions", icon: "🎉", description: "Extend the experience. Let attendees capture memories instantly. Great for expos, weddings, brand launches & festivals." },
  { path: "corporate", label: "Corporate & Enterprise", icon: "🏢", description: "Elevate gifting & culture. Create custom photobooks for HR gifting, employee milestones, corporate events, or client appreciation." },
  { path: "education", label: "Education", icon: "🎓", description: "Celebrate milestones. Photobooks for graduation, field trips, school years, or special achievements. Simple for parents, powerful for schools." },
  { path: "fnb", label: "F&B", icon: "🍽️", description: "From dining to memory. Let customers create a memento of their experience — with QR codes at tables, packaging, or receipts." },
  { path: "hotels", label: "Hotels & Resorts", icon: "🏨", description: "Turn stays into stories. Offer guests a memorable souvenir featuring your property and their holiday photos. Drive guest satisfaction & repeat stays." },
  { path: "lifestyle", label: "Lifestyle & Retail", icon: "🛍️", description: "Drive footfall & loyalty. Create Instagrammable photo experiences linked to real printed memories — perfect for fashion, cosmetics, electronics, etc." },
  { path: "malls", label: "Malls & Marketplaces", icon: "🏬", description: "Activate public spaces. Engage visitors with photo zones, events, or purchase-linked photobooks. Great for seasonal traffic and loyalty campaigns." },
  { path: "tourism", label: "Travel & Tourism", icon: "🗺️", description: "Capture the journey. From landmarks to local experiences, make it easy for travelers to print and cherish their adventure." },
  { path: "places", label: "Places of Interest", icon: "🎡", description: "Interactive memorabilia. Museums, zoos, aquariums, parks — add a new revenue stream and visitor engagement point with instant keepsakes." },
  { path: "nightlife", label: "Nightlife & Entertainment", icon: "🕺", description: "Preserve the electric atmosphere of nightlife with vibrant photobooks that let guests relive their dance floor memories." },
  { path: "nonprofit", label: "Non-profit & NGO", icon: "🤝", description: "Amplify your mission with meaningful photobooks that showcase impact stories, donor appreciation, and community connections." }
];

function Home() {
  const [activeForm, setActiveForm] = useState('enquiry'); // 'enquiry' or 'partnership'
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [currentHeroImageIndex, setCurrentHeroImageIndex] = useState(0);
  const heroRef = useRef(null);
  const nextSectionRef = useRef(null);
  const [hasScrolledPast, setHasScrolledPast] = useState(false);

  const forms = {
    enquiry: {
      id: 'fs_e987f768aea41db299f44540eceb634082ad62741daccca8e71b5fa74b81e5f6',
      src: 'https://pbww.myfreshworks.com/crm/sales/web_forms/e987f768aea41db299f44540eceb634082ad62741daccca8e71b5fa74b81e5f6/form.js',
      label: 'Events Enquiry'
    },
    partnership: {
      id: 'fs_d59f06ce35c529522257605cdb80309e3273852684d1896785eaad43eb4a4b7d',
      src: 'https://pbww.myfreshworks.com/crm/sales/web_forms/d59f06ce35c529522257605cdb80309e3273852684d1896785eaad43eb4a4b7d/form.js',
      label: 'Partnership Enquiry'
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHasScrolledPast(!entry.isIntersecting);
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.7
      }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, [hasScrolledPast]);

  useEffect(() => {
    // Hero carousel timer
    const interval = setInterval(() => {
      setCurrentHeroImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Helper function to inject script
    const injectFormScript = (formConfig, containerId) => {
      if (document.getElementById(formConfig.id)) return; // Already injected

      const script = document.createElement('script');
      script.src = formConfig.src;
      script.id = formConfig.id;
      script.crossOrigin = "anonymous";
      
      const container = document.getElementById(containerId);
      if (container) {
        container.appendChild(script);
      }
    };

    // Inject BOTH forms immediately on mount
    injectFormScript(forms.enquiry, 'freshworks-crm-container-enquiry');
    injectFormScript(forms.partnership, 'freshworks-crm-container-partnership');

    // Cleanup on final unmount
    return () => {
      Object.values(forms).forEach(form => {
        const script = document.getElementById(form.id);
        if (script && script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
      
      const injectedContainers = document.querySelectorAll('.fs-webform-container');
      injectedContainers.forEach(el => el.remove());
      const injectedLoaders = document.querySelectorAll('.fs-webform-loader');
      injectedLoaders.forEach(el => el.remove());
      
      delete window.WebFormQueue;
    };
  }, []); // Run only once on mount

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} data-section="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Image Carousel Background */}
        <div className="absolute top-0 left-0 w-full h-full z-0">
          <AnimatePresence initial={false}>
            <motion.img
              key={currentHeroImageIndex}
              src={heroImages[currentHeroImageIndex]}
              alt="Hero Background"
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100vw',
                height: '56.25vw', // 16:9 aspect ratio
                minHeight: '100vh',
                minWidth: '177.77vh', // 16:9 aspect ratio
                transform: 'translate(-50%, -50%)'
              }}
            />
          </AnimatePresence>
        </div>
        
        {/* Frosted Overlay */}
        <div 
          className="absolute top-0 left-0 w-full h-full z-10 backdrop-blur-sm"
          style={{ 
            background: 'rgba(0,0,0,0.4)',
            filter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)'
          }}
        ></div>
        
        {/* Content */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Make Every Moment Count
            <br></br>
            Instantly
          </motion.h1>
          <motion.p 
            className="text-lg sm:text-xl lg:text-2xl mb-8 sm:mb-10 text-gray-100 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Turn real-world experiences into lasting branded keepsakes with our Instant Photobook Solution.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Link to="/contact" className="btn-primary text-center px-16 py-4 text-lg">Get Started</Link>
          </motion.div>
        </div>
      </section> 
      {/* How It Works Section */}
      <section ref={nextSectionRef} data-section="how-it-works" id="how-it-works" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">Get Started in 4 Simple Steps</h2>
            <p className="section-subtitle">Launch your branded photobook experience faster than you think</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="card-highlight text-center p-8">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Discover Your Perfect Fit</h3>
              <p className="text-gray-800">We analyze your business needs and recommend the ideal photobook solution for maximum impact</p>
            </div>
            
            <div className="card-highlight text-center p-8">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">🎨</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Custom Design & Setup</h3>
              <p className="text-gray-800">Our team creates branded templates and landing pages that perfectly match your brand identity</p>
            </div>
            
            <div className="card-highlight text-center p-8">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">✅</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Review & Approve</h3>
              <p className="text-gray-800">Preview your complete photobook experience and confirm everything meets your expectations</p>
            </div>

            <div className="card-highlight text-center p-8">
              <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-primary-600 text-2xl">🚀</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Launch</h3>
              <p className="text-gray-800">Turn your brand into lasting memories , launch your photobook experience today.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section data-section="industries" id="industries" className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="section-title">Solutions by Industry</h2>
            <p className="section-subtitle">Moments by Photobook transforms everyday interactions into lasting impressions. Explore how our Instant Photobook Solution adapts to your industry:</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.path}
                className="card-hover p-4 sm:p-6 border border-gray-200 rounded-lg hover:shadow-lg transition-all duration-300 hover:border-primary-300 overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-center">
                  <div className="mb-3 sm:mb-4 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6">
                    <img 
                      src={industryThumbnails[industry.path]} 
                      alt={industry.label}
                      className="w-full h-32 sm:h-40 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                    <div className="text-3xl sm:text-4xl hidden py-8">{industry.icon}</div>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold mb-2 sm:mb-3 text-gray-800">{industry.label}</h3>
                  <p className="text-xs sm:text-sm text-gray-800 leading-relaxed">{industry.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-section="why-choose-us" className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="section-title">Why Brands Choose Us</h2>
            <p className="section-subtitle">Discover what makes our photobook solution the perfect choice for your business</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 sm:gap-8">
            <div className="card-highlight text-center p-4 sm:p-6 lg:p-8">
              <div className="bg-primary-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-primary-600 text-xl sm:text-2xl">⚡</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Lightning Fast</h3>
              <p className="text-sm sm:text-base text-gray-800">From scan to print in under 5 minutes</p>
            </div>
            <div className="card-highlight text-center p-4 sm:p-6 lg:p-8">
              <div className="bg-primary-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-primary-600 text-xl sm:text-2xl">🔄</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Seamless Integration</h3>
              <p className="text-sm sm:text-base text-gray-800">Our solution integrates with your existing systems and workflows</p>
            </div>
            
            <div className="card-highlight text-center p-4 sm:p-6 lg:p-8">
              <div className="bg-primary-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-primary-600 text-xl sm:text-2xl">🎨</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Fully Customizable</h3>
              <p className="text-sm sm:text-base text-gray-800">White-labeled solutions that match your brand identity perfectly</p>
            </div>
            
            <div className="card-highlight text-center p-4 sm:p-6 lg:p-8">
              <div className="bg-primary-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-primary-600 text-xl sm:text-2xl">📊</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">Measurable ROI</h3>
              <p className="text-sm sm:text-base text-gray-800">Track engagement, customer satisfaction, and business impact</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section data-section="about" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="section-title">About Us</h2>
            <p className="section-subtitle">Our mission and vision</p>
          </div>
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 sm:mb-10 md:mb-0">
              <p className="text-lg sm:text-xl mb-4 sm:mb-6">To turn micro-moments into macro-memories, connecting people with experiences and the brands that shape them.</p>
              <p className="text-sm sm:text-base mb-4 sm:mb-6">Moments by Photobook is a division of Photobook Worldwide – a global leader in photo printing.</p>
              <Link to="/about" className="btn-primary inline-block">Learn More About Us</Link>
            </div>
            <div className="md:w-1/2 md:pl-12">
              <img src="/images/aboutus2.jpg" alt="About Moments by Photobook" className="rounded-lg shadow-xl" />
            </div>
          </div>
        </div>
      </section>

      <CalendarModal isOpen={isCalendarModalOpen} onClose={() => setIsCalendarModalOpen(false)} />
      
      {/* Freshworks CRM Form Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white border-t border-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">Get in Touch with Us</h2>
            <p className="section-subtitle">Have questions? Choose the form that best fits your needs.</p>
          </div>

          <div className="max-w-3xl mx-auto relative bg-white rounded-xl shadow-lg border border-gray-100 p-1 min-h-[750px] flex flex-col">
            {/* Selection Buttons at the Top of the container */}
            <div className="px-6 py-8 border-b border-gray-100 flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 bg-gray-50/50 rounded-t-xl">
              <button
                type="button"
                role="button"
                aria-label="Select Events Enquiry form"
                data-form-target="enquiry"
                onClick={() => setActiveForm('enquiry')}
                className={`w-full sm:w-auto min-w-[200px] min-h-[44px] px-6 py-3 rounded-lg font-medium text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 active:scale-[0.98] flex items-center justify-center ${
                  activeForm === 'enquiry'
                    ? 'bg-primary-600 text-white shadow-md transform translate-y-[-2px]'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 cursor-pointer shadow-sm'
                }`}
              >
                {activeForm === 'enquiry' && <span className="mr-2">✓</span>}
                Events Enquiry
              </button>
              <button
                type="button"
                role="button"
                aria-label="Select Partnership Enquiry form"
                data-form-target="partnership"
                onClick={() => setActiveForm('partnership')}
                className={`w-full sm:w-auto min-w-[200px] min-h-[44px] px-6 py-3 rounded-lg font-medium text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 active:scale-[0.98] flex items-center justify-center ${
                  activeForm === 'partnership'
                    ? 'bg-primary-600 text-white shadow-md transform translate-y-[-2px]'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 cursor-pointer shadow-sm'
                }`}
              >
                {activeForm === 'partnership' && <span className="mr-2">✓</span>}
                Partnership Enquiry
              </button>
            </div>

            {/* Form Containers with Transitions */}
            <div className="relative flex-grow overflow-hidden">
              <motion.div
                key="enquiry-form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ 
                  opacity: activeForm === 'enquiry' ? 1 : 0, 
                  x: activeForm === 'enquiry' ? 0 : -20,
                  display: activeForm === 'enquiry' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="w-full h-full"
                data-form-id="enquiry"
                data-active-form={activeForm === 'enquiry'}
              >
                <div id="freshworks-crm-container-enquiry" className="w-full h-full"></div>
              </motion.div>

              <motion.div
                key="partnership-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ 
                  opacity: activeForm === 'partnership' ? 1 : 0, 
                  x: activeForm === 'partnership' ? 0 : 20,
                  display: activeForm === 'partnership' ? 'block' : 'none'
                }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className="w-full h-full"
                data-form-id="partnership"
                data-active-form={activeForm === 'partnership'}
              >
                <div id="freshworks-crm-container-partnership" className="w-full h-full"></div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
